import { useState } from "react";
import styled from "styled-components";
import SelectMulti from "react-select";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";
import { BtnGeneral } from "../../../../styles/Buttons";
import { useExcludedQuestions } from "../../../../hooks/masters/useExcludedQuestions";
import { Page, Right, HeaderRow } from "../styles/component.style";

const inp = {
  width: "100%",
  padding: "6px",
  marginBottom: "12px",
  borderRadius: "6px",
  border: "1px solid #cbd5e1",
  fontSize: "12px",
  boxSizing: "border-box",
  outline: "none",
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "36px",
    borderRadius: "6px",
    border: inp.border,
    boxShadow: state.isFocused ? "0 0 0 1px #1e40af" : "none",
    fontSize: inp.fontSize,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 6px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "34px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "2px 6px",
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "2px 6px",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    fontSize: inp.fontSize,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: inp.fontSize,
    backgroundColor: state.isSelected ? "#1e40af" : state.isFocused ? "#e2e8f0" : "white",
    color: state.isSelected ? "white" : "#0f172a",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#64748b",
  }),
};

function Field({ lbl, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "5px", width: "100%" }}>
      <label style={{ fontSize: "12px", color: "#64748b", fontWeight: "600" }}>
        {lbl}
        {required && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

export default function ExcludedQuestionsBySubgroup() {
  const {
    groups,
    subgroups,
    questions,
    relations,
    selectedGroup,
    selectedSubgroup,
    selectedQuestionIds,
    loadingRelations,
    loadingQuestions,
    saving,
    deleting,
    hasLoadedRelations,
    setSelectedQuestionIds,
    handleGroupChange,
    handleSubgroupChange,
    saveRelations,
    deleteRelation,
  } = useExcludedQuestions();

  const [relationToDelete, setRelationToDelete] = useState(null);

  const questionOptions = questions.map((question) => ({
    value: question.Pregunta,
    label: question.Pregunta,
  }));

  const selectedQuestionOptions = questionOptions.filter((option) =>
    selectedQuestionIds.includes(option.value)
  );

  const handleOpenDeleteConfirm = (relation) => {
    setRelationToDelete(relation);
  };

  const handleCloseDeleteConfirm = () => {
    if (deleting) return;
    setRelationToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!relationToDelete) return;
    await deleteRelation(relationToDelete);
    setRelationToDelete(null);
  };

  return (
    <Page>
      <Right style={{ overflowY: "auto", padding: "14px 16px" }}>
        <HeaderRow style={{ marginBottom: "12px" }}>
          <div>
            <Title>Preguntas excluidas por subgrupo</Title>
          </div>
          <BtnGeneral
            bg="green"
            padding="6px 12px"
            radius="6px"
            fw="600"
            onClick={saveRelations}
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar"}
          </BtnGeneral>
        </HeaderRow>

        <SectionCard>
          <FormRow>
            <Field lbl="Grupo" required>
              <select
                style={inp}
                value={selectedGroup}
                onChange={(e) => handleGroupChange(e.target.value)}
              >
                <option value="">Seleccione Grupo...</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.codigo} - {group.descripcion}
                  </option>
                ))}
              </select>
            </Field>

            <Field lbl="Subgrupo dependiente" required>
              <select
                style={inp}
                value={selectedSubgroup}
                onChange={(e) => handleSubgroupChange(e.target.value)}
                disabled={!selectedGroup}
              >
                <option value="">Seleccione Subgrupo...</option>
                {subgroups.map((subgroup) => (
                  <option key={subgroup.id} value={subgroup.id}>
                    {subgroup.codigo} - {subgroup.descripcion}
                  </option>
                ))}
              </select>
            </Field>
          </FormRow>

          <FormRowSingle>
            <Field lbl="Preguntas a excluir" required>
              <SelectMulti
                isMulti
                isDisabled={!selectedSubgroup}
                isLoading={loadingQuestions}
                options={questionOptions}
                value={selectedQuestionOptions}
                placeholder={
                  selectedSubgroup
                    ? "Seleccione una o varias preguntas..."
                    : "Seleccione un subgrupo primero"
                }
                styles={selectStyles}
                onChange={(selected) =>
                  setSelectedQuestionIds((selected || []).map((option) => option.value))
                }
                noOptionsMessage={() => "No hay preguntas disponibles"}
              />
            </Field>
          </FormRowSingle>
        </SectionCard>

        <div
          style={{
            maxHeight: "350px",
            overflowY: "auto",
            border: "1px solid #e5e7eb",
            borderRadius: "6px",
            marginTop: "12px",
          }}
        >
          <table style={tableStyle}>
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "#f8fafc",
                zIndex: 1,
              }}
            >
              <tr>
                <th style={{ ...thTdStyle, width: "220px" }}>Grupo</th>
                <th style={{ ...thTdStyle, width: "220px" }}>Subgrupo</th>
                <th style={thTdStyle}>Pregunta</th>
                <th style={{ ...thTdStyle, width: "100px", textAlign: "center" }}>Accion</th>
              </tr>
            </thead>

            <tbody>
              {!selectedSubgroup ? (
                <tr>
                  <td colSpan="4" style={emptyStateStyle}>
                    Seleccione un subgrupo para visualizar las relaciones configuradas.
                  </td>
                </tr>
              ) : loadingRelations ? (
                <tr>
                  <td colSpan="4" style={emptyStateStyle}>
                    Cargando relaciones...
                  </td>
                </tr>
              ) : relations.length > 0 ? (
                relations.map((relation) => (
                  <tr key={`${relation.IdSubgrupo}-${relation.IdPregunta}`}>
                    <td style={thTdStyle}>{relation.Grupo}</td>
                    <td style={thTdStyle}>
                      [{relation.CodigoSubgrupo}] {relation.Subgrupo}
                    </td>
                    <td style={thTdStyle}>
                      {relation.IdPregunta} - {relation.Pregunta}
                    </td>
                    <td style={{ ...thTdStyle, textAlign: "center" }}>
                      <button
                        type="button"
                        style={deleteBtnStyle}
                        onClick={() => handleOpenDeleteConfirm(relation)}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))
              ) : hasLoadedRelations ? (
                <tr>
                  <td colSpan="4" style={emptyStateStyle}>
                    No hay relaciones configuradas para el subgrupo seleccionado.
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan="4" style={emptyStateStyle}>
                    Seleccione un subgrupo para visualizar las relaciones configuradas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ConfirmModal
          isOpen={Boolean(relationToDelete)}
          onClose={handleCloseDeleteConfirm}
          onConfirm={handleConfirmDelete}
          title="Confirmar eliminacion"
          message={
            relationToDelete
              ? `Desea eliminar la exclusion de la pregunta "${relationToDelete.IdPregunta} - ${relationToDelete.Pregunta}" para el subgrupo "${relationToDelete.Subgrupo}"?`
              : ""
          }
          confirmText="Confirmar"
          cancelText="Cancelar"
          confirmColor="red"
          loading={deleting}
        />
      </Right>
    </Page>
  );
}

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  color: var(--color-text-title);
`;

const SectionCard = styled.div`
  margin-bottom: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 14px 14px 10px;
  background: #fff;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-bottom: 12px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const FormRowSingle = styled.div`
  display: block;
  margin-bottom: 12px;
`;

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
};

const thTdStyle = {
  border: "1px solid #e5e7eb",
  padding: "6px",
  fontSize: "13px",
};

const emptyStateStyle = {
  textAlign: "center",
  padding: "14px",
  color: "#64748b",
  fontSize: "13px",
};

const deleteBtnStyle = {
  border: "none",
  background: "#fee2e2",
  color: "#b91c1c",
  borderRadius: "4px",
  padding: "4px 8px",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: "600",
};
